import { defineStore } from 'pinia';
import { computed, reactive, Component } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { useQueryStatus } from '@/Composables/useQueryStatus';
import { KrakenSDK } from '@agentsoftware/kraken-ts-sdk';
import { ListingType, MultiplePropertyListingsUserFields, MultiplePropertyTransactionUserFields } from '@agentsoftware/kraken-ts-sdk/sdk/models/shared';
import {
  cloneDeep, pickBy, slice, uniqBy,
} from 'lodash-es';
import formattedDisplayAddress from '@/Support/FormattedDisplayAddress';
import { trackEvent } from '@/Support/MixpanelTracking';
// eslint-disable-next-line import/no-cycle
import { ComparableTypes, useComparablesStore } from '@/Stores/Comparables';
// eslint-disable-next-line import/no-cycle
import { useStreetViewStore } from '@/Stores/StreetView';
// eslint-disable-next-line import/no-cycle
import { useReportEditStore } from '@/Stores/ReportEdit';
// eslint-disable-next-line import/no-cycle
import { useMarketStatsStore } from '@/Stores/MarketStats';
import defaultReportState from '@/Config/ReportState';
import { useToggle } from '@vueuse/core';
import usePageScroll from '@/Composables/usePageScroll';
import PropertyImageSource = App.Enums.PropertyImageSource;
import ComparablesDetailsData = App.Data.Report.Comparables.ComparablesDetailsData;
import ReportTabEnum = App.Enums.ReportTab;
import ReportData = App.Data.Report.ReportData;
import ReportContentData = App.Data.Report.ReportContentData;
import ReportTemplateData = App.Data.ReportTemplate.ReportTemplateData;
import StatsData = App.Data.Report.Market.StatsData;
import EstimatedPriceChangeStatsData = App.Data.Report.Market.EstimatedPriceChangeStatsData;
import ReportParkingType = App.Enums.ReportParkingType;

export type OrderableTabs = Pick<App.Data.Report.ReportContentData, 'overview' | 'comparables' | 'localArea' | 'gallery' | 'market' | 'about'>;

export type OrderableSubTabs = Pick<App.Data.Report.ReportOverviewData, 'propertyValue' | 'marketHistory' | 'build' | 'propertySize' | 'plotInformation' | 'councilTax' | 'tenure' | 'floodRisk' | 'mobileCoverage' | 'broadbandSpeed' | 'epc' | 'planningApplications' | 'listedBuildings' | 'description' | 'constructionMaterials' | 'utilities' | 'propertyRestrictions' | 'coastalErosion' | 'outdoorSpace' | 'treePreservationOrders' | 'airTrafficNoise' | 'parking'>
| Pick<App.Data.Report.ReportComparablesData, 'sales' | 'lettings' | 'completed'>
| Pick<App.Data.Report.ReportLocalAreaData, 'education' | 'transport' | 'nearbyPlanningApplications' | 'nearbyListedBuildings'>
| Pick<App.Data.Report.ReportGalleryData, 'uploadedImages' | 'pastListingImages' | 'videoAndVirtualTours'>
| Pick<App.Data.Report.ReportMarketData, 'sales' | 'lettings'>
| Pick<App.Data.Report.ReportAboutData, 'agencyBio' | 'agentBio' | 'contact' | 'testimonials' | 'video'>;

export type ReportTab = App.Data.Report.ReportOverviewData
| App.Data.Report.ReportComparablesData
| App.Data.Report.ReportLocalAreaData
| App.Data.Report.ReportGalleryData
| App.Data.Report.ReportAboutData;

export type ReportSubSection = App.Data.Report.Overview.ValuationRequestData
| App.Data.Report.Overview.PropertyValueData
| App.Data.Report.Overview.EpcData
| App.Data.Report.Overview.BroadbandSpeedData
| App.Data.Report.Overview.BuildData
| App.Data.Report.Overview.CoastalErosionData
| App.Data.Report.Overview.ConstructionMaterialData
| App.Data.Report.Overview.CouncilTaxData
| App.Data.Report.Overview.FloodRiskData
| App.Data.Report.Overview.ListedBuildingsData
| App.Data.Report.Overview.MarketHistoryData
| App.Data.Report.Overview.MobileCoverageData
| App.Data.Report.Overview.OutdoorSpaceData
| App.Data.Report.Overview.ParkingData
| App.Data.Report.Overview.PlanningApplicationsData
| App.Data.Report.Overview.PlotInformationData
| App.Data.Report.Overview.PropertyDescriptionData
| App.Data.Report.Overview.PropertyRestrictionsData
| App.Data.Report.Overview.PropertySizeData
| App.Data.Report.Overview.TenureData
| App.Data.Report.Overview.UtilitiesData
| App.Data.Report.Comparables.ComparablesData
| App.Data.Report.LocalArea.EducationData
| App.Data.Report.LocalArea.TransportData
| App.Data.Report.LocalArea.NearbyListedBuildingsData
| App.Data.Report.LocalArea.NearbyPlanningApplicationsData
| App.Data.Report.Gallery.UploadedImagesData
| App.Data.Report.Gallery.PastListingImagesData
| App.Data.Report.Gallery.VideoAndVirtualToursData
| App.Data.Report.Market.Sales.AverageTimeOnMarketData
| App.Data.Report.Market.Sales.VolumeOfSoldPropertiesData
| App.Data.Report.Market.Sales.EstimatedPriceChangeData
| App.Data.Report.Market.Sales.AveragePricePerSqftData
| App.Data.Report.Market.Lettings.AverageTimeOnMarketData
| App.Data.Report.Market.Lettings.EstimatedPriceChangeData
| App.Data.Report.Market.Lettings.AveragePropertyYieldData
| App.Data.Report.Market.Lettings.VolumeOfLetPropertiesData
| App.Data.Report.About.ContactData
| App.Data.Report.About.AgencyBioData
| App.Data.Report.About.TestimonialsData
| App.Data.Report.About.AgentBioData
| App.Data.Report.About.AgencyVideoData;

type OverviewSections = keyof Omit<App.Data.Report.ReportOverviewData, 'display' | 'plot' | 'mainImage' | 'mainImageSource' | 'titleDeeds'>;

type ComparablesSections = keyof Omit<App.Data.Report.ReportComparablesData, 'display'>;

type LocalAreaSections = keyof Omit<App.Data.Report.ReportLocalAreaData, 'display'>;

type GallerySections = keyof Omit<App.Data.Report.ReportGalleryData, 'display'>;

type AboutSections = keyof Omit<App.Data.Report.ReportAboutData, 'display'>;

type MarketSections = keyof Omit<App.Data.Report.ReportMarketData, 'display'>;

type SectionId = OverviewSections | ComparablesSections | LocalAreaSections | GallerySections | AboutSections | MarketSections;

export type ReportComponent = {
  id: SectionId;
  name: Component;
  display: boolean;
  order: number;
  props?: Record<string, any>;
};

// eslint-disable-next-line import/prefer-default-export
export const useReportStore = defineStore('report', () => {
  const page = usePage();

  const streetViewStore = useStreetViewStore();
  const reportEditStore = useReportEditStore();
  const marketStatsStore = useMarketStatsStore();
  const comparablesStore = useComparablesStore();

  const propertyImageQueryStatus = useQueryStatus();
  const saleComparablesQueryStatus = useQueryStatus();
  const completedComparablesQueryStatus = useQueryStatus();
  const rentalComparablesQueryStatus = useQueryStatus();
  const reportLoadedQueryStatus = useQueryStatus();
  const reportSaveQueryStatus = useQueryStatus();

  const [hasStalePdf, toggleHasStalePdf] = useToggle<boolean>();
  const [preserveState, setPreserveState] = useToggle<boolean>(false);

  const kraken = computed(() => new KrakenSDK({ serverURL: page.props.config.kraken.api_url }));

  type ReportState = {
    public: boolean;
    hideCover: boolean;
    showMainImage: boolean;
    id: string | null,
    digitalShortUrl: string;
    digitalQrCode: string;
    leadCaptureShortUrl: string;
    leadCaptureQrCode: string;
    pdfUrl: string | null;
    accountId: string | null;
    agentId: string | null;
    templateId: string | null;
    currentTab: ReportTabEnum;
    selectedTab: ReportTabEnum | null;
    bookViewingUrl?: string;
    content: App.Data.Report.ReportContentData;
  };

  const initialState = () => (cloneDeep(defaultReportState));

  const { resetScrollPosition } = usePageScroll();

  const state = reactive<ReportState>(initialState());

  // Computed
  const accountId = computed(() => state.accountId);

  const bookViewingUrl = computed(() => state.bookViewingUrl);

  const agentId = computed(() => state.agentId);

  const isPublicReport = computed(() => state.public);

  const showMainImage = computed(() => state.showMainImage);

  const reportId = computed(() => state.id);

  const templateId = computed(() => state.templateId);

  const propertyId = computed(() => state.content.streetGroupPropertyId);

  const reportTitle = computed(() => state.content.reportTitle);

  const address = computed(() => state.content.address);

  const location = computed(() => state.content.location);

  const overview = computed(() => state.content.overview);

  const comparables = computed(() => state.content.comparables);

  const localArea = computed(() => state.content.localArea);

  const gallery = computed(() => state.content.gallery);

  const market = computed(() => state.content.market);

  const about = computed(() => state.content.about);

  const settings = computed(() => state.content.settings);

  const currentTab = computed(() => state.currentTab);

  const selectedTab = computed(() => state.selectedTab);

  const hideCover = computed(() => state.hideCover);

  const pricePerSqft = computed(() => {
    if (overview.value.propertyValue.estimatedValue && overview.value.propertySize.floorAreaSquareFeet) {
      return Math.round(overview.value.propertyValue.estimatedValue / overview.value.propertySize.floorAreaSquareFeet);
    }
    return null;
  });

  const pricePerSqm = computed(() => {
    if (overview.value.propertyValue.estimatedValue && overview.value.propertySize.floorArea) {
      return Math.round(overview.value.propertyValue.estimatedValue / overview.value.propertySize.floorArea);
    }
    return null;
  });

  const marketHistory = computed(() => overview.value?.marketHistory?.content?.slice().reverse());

  const polygons = computed(() => overview.value?.plot);

  const digitalShortUrl = computed(() => state.digitalShortUrl);

  const digitalQrCode = computed(() => state.digitalQrCode);

  const leadCaptureShortUrl = computed(() => state.leadCaptureShortUrl);

  const leadCaptureQrCode = computed(() => state.leadCaptureQrCode);

  // Functions
  const setTenureLeaseTermValues = () => {
    if (overview.value?.tenure?.leaseDetails?.leaseTermYears || overview.value?.tenure?.leaseDetails?.leaseTermMonths) {
      return;
    }

    const leaseTermInDays = overview.value?.tenure?.leaseDetails?.leaseTermInDays;

    if (!leaseTermInDays || !overview.value?.tenure?.leaseDetails) {
      return;
    }

    if (leaseTermInDays > 3650) {
      overview.value.tenure.leaseDetails.leaseTermYears = Math.floor(leaseTermInDays / 365);
      overview.value.tenure.leaseDetails.leaseTermMonths = null;
    }

    overview.value.tenure.leaseDetails.leaseTermYears = Math.floor(leaseTermInDays / 365);
    overview.value.tenure.leaseDetails.leaseTermMonths = Math.round((leaseTermInDays % 365) / 30);
  };

  const setTenureTimeRemainingValues = () => {
    if (overview.value?.tenure?.leaseDetails?.timeRemainingYears || overview.value?.tenure?.leaseDetails?.timeRemainingMonths) {
      return;
    }

    const timeRemainingInDays = overview.value?.tenure?.leaseDetails?.timeRemainingInDays;

    if (!timeRemainingInDays || !overview.value?.tenure?.leaseDetails) {
      return;
    }

    if (timeRemainingInDays < 365) {
      overview.value.tenure.leaseDetails.timeRemainingMonths = Math.round(timeRemainingInDays / 30.4);
      overview.value.tenure.leaseDetails.timeRemainingYears = null;
    }

    if (timeRemainingInDays > 3650) {
      overview.value.tenure.leaseDetails.timeRemainingMonths = null;
      overview.value.tenure.leaseDetails.timeRemainingYears = Math.round(timeRemainingInDays / 365);
    }

    overview.value.tenure.leaseDetails.timeRemainingYears = Math.floor(timeRemainingInDays / 365);

    overview.value.tenure.leaseDetails.timeRemainingMonths = Math.round((timeRemainingInDays % 365) / 30);
  };

  const initialiseTenureDataCard = () => {
    setTenureLeaseTermValues();
    setTenureTimeRemainingValues();
  };

  const updatePropertyImage = (source: PropertyImageSource, url: string | null) => {
    if (!state.content.overview) {
      return;
    }

    state.content.overview.mainImageSource = source;
    state.content.overview.mainImage = url;

    propertyImageQueryStatus.setStatus('success');
  };

  const updateSalesMarketStats = (section: 'salesAveragePricePerSqft' | 'salesAverageTimeOnMarket' | 'salesEstimatedPriceChange' | 'salesVolumeOfSoldProperties', data: StatsData[] | EstimatedPriceChangeStatsData[]) => {
    state.content.market.sales[section].stats = data;
  };

  const updateLettingsMarketStats = (section: 'lettingsEstimatedPriceChange' | 'lettingsAverageTimeOnMarket' | 'lettingsVolumeOfLetProperties' | 'lettingsAveragePropertyYield', data: StatsData[] | EstimatedPriceChangeStatsData[]) => {
    state.content.market.lettings[section].stats = data;
  };

  const saveRevision = (onSuccess?: () => void) => {
    router.post(route('reports.revisions.store', { report: reportId.value }), {
      agent: agentId.value,
      reportData: state.content,
    }, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        toggleHasStalePdf(true);
        onSuccess?.();
      },
    });
  };

  const saveReport = async () => {
    trackEvent('Property Report', {
      Status: 'Report saved',
      'Property ID': propertyId.value,
      Account: accountId.value,
      Company: page.props.auth.user?.company?.name ?? null,
    });

    reportSaveQueryStatus.setStatus('loading');

    router.put(route('reports.update', { report: reportId.value }), {
      agent: agentId.value,
      reportTemplate: templateId.value,
      reportData: state.content,
    }, {
      onError: () => {
        reportSaveQueryStatus.setStatus('error');
      },
      onSuccess: () => {
        reportSaveQueryStatus.setStatus('success');
      },
    });
  };

  const refreshUploadedImages = (uploadedImages: App.Data.Report.ReportImageData[]) => {
    state.content.gallery.uploadedImages.images = uniqBy([...gallery.value?.uploadedImages.images ?? [], ...uploadedImages], 'id');

    state.content.gallery.selectedUploadedImages = uniqBy([
      ...state.content.gallery.selectedUploadedImages,
      ...state.content.gallery.uploadedImages.images,
    ], 'id');
  };

  const toggleComparableSelected = (comparableProperty: App.Data.Report.Comparables.ComparablesDetailsData, comparableType: 'sales' | 'lettings' | 'completed') => {
    if (!comparables.value) {
      return;
    }

    const currentlySelected = comparables.value?.[comparableType].comparables?.find((comparable) => comparable.id === comparableProperty.id);

    if (currentlySelected) {
      comparables.value[comparableType].comparables = comparables.value[comparableType].comparables?.filter((id) => id !== currentlySelected) ?? [];
      saveRevision(() => comparablesStore.updateSelectedComparablesCount());
      return;
    }

    const selectedComparable = {
      id: comparableProperty.id ?? '',
      streetGroupPropertyId: comparableProperty.streetGroupPropertyId ?? null,
      listingType: comparableProperty.listingType,
      address: comparableProperty.address,
      image: comparableProperty.image ?? '',
      listingImages: comparableProperty.listingImages,
      price: comparableProperty.price ?? null,
      listingEvents: comparableProperty.listingEvents ?? [],
      bedrooms: comparableProperty.bedrooms,
      bathrooms: comparableProperty.bathrooms,
      type: comparableProperty.type,
      councilTaxBand: comparableProperty.councilTaxBand ?? null,
      internalArea: comparableProperty.internalArea ?? null,
      internalAreaSqFt: comparableProperty.internalAreaSqFt,
      pricePerSqFt: comparableProperty.pricePerSqFt,
      pricePerSqM: comparableProperty.pricePerSqM,
      outdoorSpace: comparableProperty.outdoorSpace ?? null,
      outdoorSpaceSqFt: comparableProperty.outdoorSpaceSqFt ?? null,
      distanceFromPoint: comparableProperty.distanceFromPoint ?? null,
      location: comparableProperty.location ?? null,
      status: comparableProperty.status,
      listedDate: comparableProperty.listedDate,
      transactionDate: null,
      agent: comparableProperty.agent,
      tenure: null,
    };

    comparables.value[comparableType].comparables = [selectedComparable, ...comparables.value[comparableType].comparables ?? []];

    saveRevision(() => comparablesStore.updateSelectedComparablesCount());
  };

  const comparableIsSelected = (id: string, comparableType: 'sales' | 'lettings' | 'completed') => !!(comparables.value ? comparables.value?.[comparableType].comparables?.find((comparable: App.Data.Report.Comparables.ComparablesDetailsData) => comparable.id === id) : false);

  /**
   * This method sets the "order" property for each Report section based off the
   * order from the ReportEditStore. This is because we use the VueDraggable
   * order value from said store to determine the correct order for a
   * section.
   */
  const setOrder = (type: 'tab' | 'subsection', tabId?: keyof OrderableTabs) => {
    if (type === 'tab') {
      reportEditStore.sections.forEach((section, index) => {
        state.content[section.id as keyof OrderableTabs].order = index + 1;
      });

      saveRevision();
      return;
    }

    const tab = reportEditStore.sections.find((section) => section.id === tabId);

    if (!tab) {
      return;
    }

    tab.subsections.forEach((subSection, subIndex) => {
      (state.content[tab.id as keyof OrderableTabs][subSection.id as keyof OrderableSubTabs] as ReportSubSection).order = subIndex + 1;
    });

    saveRevision();
  };

  const resetPropertyImage = () => {
    const pastListingExteriorImageUrl = gallery.value.pastListingImages.pastListingImages.map((listing) => listing.images).flat().find((image) => image?.name === 'Exterior')?.url;

    const imageSource = pastListingExteriorImageUrl ? 'kraken' : 'streetview';
    const imageUrl = pastListingExteriorImageUrl ?? overview.value.streetView.url;

    if (!imageUrl) {
      return;
    }

    updatePropertyImage(imageSource, imageUrl);
  };

  const setStreetViewPosition = (streetViewData: App.Data.Report.Overview.StreetViewData) => {
    state.content.overview.streetView = { ...streetViewData, display: state.content.overview.streetView.display };

    if (streetViewData.url && overview.value?.mainImageSource !== 'kraken') {
      updatePropertyImage('streetview', streetViewData.url);
    }
  };

  const updateStreetViewPosition = (streetViewData: App.Data.Report.Overview.StreetViewData) => {
    state.content.overview.streetView = { ...streetViewData, display: state.content.overview.streetView.display };

    if (streetViewData.url && overview.value?.mainImageSource !== 'kraken') {
      updatePropertyImage('streetview', streetViewData.url);
      saveRevision();
    }
  };

  const updatePropertyValueData = (propertyValue: App.Data.Report.Overview.PropertyValueData) => {
    if (state.content.overview) {
      state.content.overview.propertyValue = propertyValue;
      saveRevision();
    }
  };

  const updateDescriptionData = (description: App.Data.Report.Overview.PropertyDescriptionData) => {
    if (state.content.overview) {
      state.content.overview.description = description;
      saveRevision();
    }
  };

  const updateConstructionMaterialData = (constructionMaterials: App.Data.Report.Overview.ConstructionMaterialData) => {
    if (state.content.overview) {
      state.content.overview.constructionMaterials = constructionMaterials;
      saveRevision();
    }
  };

  const updateUtilitiesData = (utilities: App.Data.Report.Overview.UtilitiesData) => {
    if (state.content.overview) {
      state.content.overview.utilities = utilities;
      saveRevision();
    }
  };

  const updateOutdoorSpaceData = (outdoorSpace: App.Data.Report.Overview.OutdoorSpaceData) => {
    if (state.content.overview) {
      state.content.overview.outdoorSpace = outdoorSpace;
      saveRevision();
    }
  };

  const updatePropertySizeData = (propertySize: App.Data.Report.Overview.PropertySizeData) => {
    if (state.content.overview) {
      state.content.overview.propertySize = propertySize;
      saveRevision();
    }
  };

  const updateBuildData = (build: App.Data.Report.Overview.BuildData) => {
    if (state.content.overview) {
      state.content.overview.build = build;
      saveRevision();
    }
  };

  const updateCouncilTaxData = (councilTax: App.Data.Report.Overview.CouncilTaxData) => {
    if (state.content.overview) {
      state.content.overview.councilTax = councilTax;
      saveRevision();
    }
  };

  const updateTenureData = (tenure: App.Data.Report.Overview.TenureData) => {
    if (state.content.overview) {
      state.content.overview.tenure = { ...tenure };
      saveRevision();
    }
  };

  const updateTitleDeedsData = (titleDeeds: App.Data.Report.Overview.TitleDeedData[]) => {
    if (state.content.overview) {
      state.content.overview.titleDeeds = titleDeeds;
      saveRevision();
    }
  };

  const toggleShowMainImage = (value: boolean) => {
    state.showMainImage = value;
  };

  const getMinMaxFilterValue = (listingType: 'sales' | 'lettings') => {
    if (listingType === 'sales' && state.content.overview.propertyValue.estimatedValue) {
      return {
        min: state.content.overview.propertyValue.estimatedValue * 0.8,
        max: state.content.overview.propertyValue.estimatedValue * 1.2,
      };
    }

    if (listingType === 'lettings' && state.content.overview.propertyValue.estimatedRentalValue) {
      return {
        min: state.content.overview.propertyValue.estimatedRentalValue * 0.8,
        max: state.content.overview.propertyValue.estimatedRentalValue * 1.2,
      };
    }

    return null;
  };

  const fetchComparables = async (listingType: 'sales' | 'lettings') => {
    if (!location.value) {
      return { [listingType]: null };
    }

    const comparablesRequestParameters = pickBy({
      point: [`${location.value.lat.toString()},${location.value.lng.toString()}`],
      radius: 20000,
      filterStatus: listingType === 'sales' ? ['Sold STC', 'Available', 'Under Offer', 'Reserved'] : ['Available', 'Let Agreed'],
      filterPropertyType: overview.value?.build?.propertyType ? [overview.value.build.propertyType] : null,
      filterListingType: listingType === 'sales' ? ListingType.Sale : ListingType.Rental,
      filterMinPrice: getMinMaxFilterValue(listingType)?.min ?? null,
      filterMaxPrice: getMinMaxFilterValue(listingType)?.max ?? null,
      results: 8,
    });

    try {
      const response = await kraken.value.areas
        .getPropertyListingsInAreaAreasPropertyListingsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            sort: 'distance_from_point',
            fieldsListing: [
              MultiplePropertyListingsUserFields.Address,
              MultiplePropertyListingsUserFields.Agent,
              MultiplePropertyListingsUserFields.CouncilTaxBand,
              MultiplePropertyListingsUserFields.Description,
              MultiplePropertyListingsUserFields.DisplayAddress,
              MultiplePropertyListingsUserFields.DistanceFromPoint,
              MultiplePropertyListingsUserFields.EnergyEfficiencyRating,
              MultiplePropertyListingsUserFields.EstimatedValue,
              MultiplePropertyListingsUserFields.EstimatedValueRounded,
              MultiplePropertyListingsUserFields.GardenDirection,
              MultiplePropertyListingsUserFields.HasReduction,
              MultiplePropertyListingsUserFields.Images,
              MultiplePropertyListingsUserFields.InternalAreaSquareMetres,
              MultiplePropertyListingsUserFields.IsBungalow,
              MultiplePropertyListingsUserFields.ListedDate,
              MultiplePropertyListingsUserFields.ListingEvents,
              MultiplePropertyListingsUserFields.ListingId,
              MultiplePropertyListingsUserFields.ListingQuality,
              MultiplePropertyListingsUserFields.ListingType,
              MultiplePropertyListingsUserFields.Location,
              MultiplePropertyListingsUserFields.MainImageUrl,
              MultiplePropertyListingsUserFields.NumberOfBathrooms,
              MultiplePropertyListingsUserFields.NumberOfBedrooms,
              MultiplePropertyListingsUserFields.OriginalPrice,
              MultiplePropertyListingsUserFields.OutdoorSpaceAreaSquareMetres,
              MultiplePropertyListingsUserFields.Ownership,
              MultiplePropertyListingsUserFields.Price,
              MultiplePropertyListingsUserFields.PropertyType,
              MultiplePropertyListingsUserFields.Source,
              MultiplePropertyListingsUserFields.Status,
              MultiplePropertyListingsUserFields.StreetGroupPropertyId,
              MultiplePropertyListingsUserFields.Tenure,
              MultiplePropertyListingsUserFields.WeeklyPrice,
              MultiplePropertyListingsUserFields.YearBuilt,
              MultiplePropertyListingsUserFields.YearsAtProperty,
            ],
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        );

      state.content.comparables[listingType].isEmpty = !response?.data?.length;

      if (listingType === 'sales') {
        saleComparablesQueryStatus.setStatus('success');
      }

      if (listingType === 'lettings') {
        rentalComparablesQueryStatus.setStatus('success');
      }

      const currentPropertyExcluded = response?.data.filter((property) => property.attributes.streetGroupPropertyId !== propertyId.value);

      return { [listingType]: currentPropertyExcluded };
    } catch (e: any) {
      if (listingType === 'sales') {
        e.status === 404 ? saleComparablesQueryStatus.setStatus('success') : saleComparablesQueryStatus.setStatus('error');
      }

      if (listingType === 'lettings') {
        e.status === 404 ? rentalComparablesQueryStatus.setStatus('success') : rentalComparablesQueryStatus.setStatus('error');
      }
      return { [listingType]: null };
    }
  };

  const fetchCompletedComparables = async () => {
    if (!location.value) {
      return { completed: null };
    }

    const comparablesRequestParameters = pickBy({
      point: [`${location.value.lat.toString()},${location.value.lng.toString()}`],
      radius: 20000,
      filterStartMonth: comparablesStore.filterOptions.completedSinceOptions[1].id,
      filterPropertyType: overview.value?.build?.propertyType ? [overview.value.build.propertyType] : null,
      filterMinPrice: state.content.overview.propertyValue.estimatedValue ? state.content.overview.propertyValue.estimatedValue * 0.8 : null,
      filterMaxPrice: state.content.overview.propertyValue.estimatedValue ? state.content.overview.propertyValue.estimatedValue * 1.2 : null,
      filterMaxDaysOnMarket: 1095,
      results: 8,
    });

    try {
      const response = await kraken.value.areas
        .getPropertyTransactionsInAreaAreasPropertyTransactionsGet(
          { xApiKey: page.props.config.kraken.api_key },
          {
            ...comparablesRequestParameters,
            sort: 'distance',
            fieldsTransaction: [
              MultiplePropertyTransactionUserFields.Address,
              MultiplePropertyTransactionUserFields.StreetGroupPropertyId,
              MultiplePropertyTransactionUserFields.Address,
              MultiplePropertyTransactionUserFields.TransactionId,
              MultiplePropertyTransactionUserFields.TransactionDate,
              MultiplePropertyTransactionUserFields.Price,
              MultiplePropertyTransactionUserFields.Location,
              MultiplePropertyTransactionUserFields.PropertyType,
              MultiplePropertyTransactionUserFields.IsNewBuild,
              MultiplePropertyTransactionUserFields.PricePerSquareMetre,
              MultiplePropertyTransactionUserFields.InternalAreaSquareMetres,
              MultiplePropertyTransactionUserFields.NumberOfBedrooms,
              MultiplePropertyTransactionUserFields.NumberOfBathrooms,
              MultiplePropertyTransactionUserFields.Images,
              MultiplePropertyTransactionUserFields.StreetView,
              MultiplePropertyTransactionUserFields.Tenure,
              MultiplePropertyTransactionUserFields.DistanceInMetres,
            ],
          },
          { fetchOptions: { headers: { 'Accept-Version': page.props.config.kraken.version } } },
        );

      state.content.comparables.completed.isEmpty = !response?.data?.length;

      completedComparablesQueryStatus.setStatus('success');

      const currentPropertyExcluded = response?.data.filter((property) => property.attributes.streetGroupPropertyId !== propertyId.value);

      return { completed: currentPropertyExcluded };
    } catch (e: any) {
      e.status === 404 ? completedComparablesQueryStatus.setStatus('success') : completedComparablesQueryStatus.setStatus('error');
      return { completed: null };
    }
  };

  const retrieveInitialCompletedComparables = async () => {
    completedComparablesQueryStatus.setStatus('loading');

    if (!location.value) {
      completedComparablesQueryStatus.setStatus('error');
      return;
    }

    const completedComparablesResponse = await fetchCompletedComparables();

    state.content.comparables.completed.comparables = completedComparablesResponse?.completed?.map((comparableProperty) => (
      {
        id: comparableProperty.id ?? '',
        streetGroupPropertyId: comparableProperty.attributes.streetGroupPropertyId ?? null,
        listingType: 'completed',
        address: comparableProperty.attributes.address ? formattedDisplayAddress(comparableProperty.attributes.address) : '',
        image: comparablesStore.formatCompletedImages(comparableProperty.attributes.images)[0] ?? null,
        listingImages: comparablesStore.formatCompletedImages(comparableProperty.attributes.images),
        price: comparableProperty.attributes.price as number,
        listingEvents: [],
        bedrooms: comparableProperty.attributes.numberOfBedrooms ?? null,
        bathrooms: comparableProperty.attributes.numberOfBathrooms ?? null,
        type: comparableProperty.attributes.propertyType ?? null,
        councilTaxBand: null,
        internalArea: comparableProperty.attributes.internalAreaSquareMetres ?? null,
        internalAreaSqFt: comparableProperty.attributes.internalAreaSquareMetres ? Math.round(comparableProperty.attributes.internalAreaSquareMetres * 10.7639) : null,
        pricePerSqFt: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price ? Math.round((comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres) * 10.7639) : null,
        pricePerSqM: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price ? Math.round(comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres) : null,
        outdoorSpace: null,
        outdoorSpaceSqFt: null,
        distanceFromPoint: comparableProperty.attributes.distanceInMetres ?? null,
        location: {
          lng: comparableProperty.attributes.location?.coordinates?.longitude ?? 0,
          lat: comparableProperty.attributes.location?.coordinates?.latitude ?? 0,
        },
        status: null,
        listedDate: null,
        agent: null,
        tenure: null,
        transactionDate: comparableProperty.attributes.transactionDate?.toString() ?? null,
      })) ?? [];

    if (comparables.value.completed.comparables?.length) {
      state.content.comparables.isEmpty = false;
    }
  };

  const retrieveInitialRentalComparables = async () => {
    rentalComparablesQueryStatus.setStatus('loading');

    if (!location.value) {
      rentalComparablesQueryStatus.setStatus('error');
      return;
    }

    const comparableResponse = await fetchComparables('lettings');

    state.content.comparables.lettings.comparables = comparableResponse?.lettings?.map((comparableProperty) => (
      {
        id: comparableProperty.id ?? '',
        streetGroupPropertyId: comparableProperty.attributes.streetGroupPropertyId ?? null,
        listingType: 'rental',
        address: comparableProperty.attributes.address ? formattedDisplayAddress(comparableProperty.attributes.address) : comparableProperty.attributes.displayAddress as string,
        image: comparableProperty.attributes.mainImageUrl ?? '',
        listingImages: comparableProperty.attributes.images as string[],
        price: comparableProperty.attributes.price as number ?? null,
        listingEvents: comparableProperty.attributes.listingEvents?.map((listingEvent) => ({
          eventType: listingEvent.eventType,
          date: listingEvent.date.toString(),
          price: listingEvent.relatedPrice as number,
        })) ?? null,
        bedrooms: comparableProperty.attributes.numberOfBedrooms as number,
        bathrooms: comparableProperty.attributes.numberOfBathrooms as number,
        type: comparableProperty.attributes.propertyType as string,
        councilTaxBand: comparableProperty.attributes.councilTaxBand ?? null,
        internalArea: comparableProperty.attributes.internalAreaSquareMetres ?? null,
        internalAreaSqFt: comparableProperty.attributes.internalAreaSquareMetres ? Math.round(comparableProperty.attributes.internalAreaSquareMetres * 10.7639) : null,
        pricePerSqFt: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price ? Math.round((comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres) * 10.7639) : null,
        pricePerSqM: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price ? Math.round(comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres) : null,
        outdoorSpace: comparableProperty.attributes.outdoorSpaceAreaSquareMetres ?? null,
        outdoorSpaceSqFt: comparableProperty.attributes.outdoorSpaceAreaSquareMetres ? Math.round(comparableProperty.attributes.outdoorSpaceAreaSquareMetres * 10.7639) : null,
        distanceFromPoint: comparableProperty.attributes.distanceFromPoint ?? null,
        location: {
          lng: comparableProperty.attributes.location?.coordinates?.longitude ?? 0,
          lat: comparableProperty.attributes.location?.coordinates?.latitude ?? 0,
        },
        status: comparableProperty.attributes.status as string,
        listedDate: comparableProperty.attributes.listedDate as string,
        transactionDate: null,
        agent: comparableProperty.attributes.agent as ComparablesDetailsData['agent'],
        tenure: comparableProperty.attributes.tenure as ComparablesDetailsData['tenure'],
      })) ?? [];

    if (comparables.value.lettings.comparables?.length) {
      state.content.comparables.isEmpty = false;
    }
  };

  const retrieveInitialSaleComparables = async () => {
    saleComparablesQueryStatus.setStatus('loading');

    if (!location.value) {
      saleComparablesQueryStatus.setStatus('error');
      return;
    }

    const comparableResponse = await fetchComparables('sales');

    state.content.comparables.sales.comparables = comparableResponse?.sales?.map((comparableProperty) => (
      {
        id: comparableProperty.id ?? '',
        streetGroupPropertyId: comparableProperty.attributes.streetGroupPropertyId ?? null,
        listingType: 'sale',
        address: comparableProperty.attributes.address ? formattedDisplayAddress(comparableProperty.attributes.address) : (comparableProperty.attributes.displayAddress ?? null),
        image: comparableProperty.attributes.mainImageUrl ?? '',
        listingImages: comparableProperty.attributes.images ?? null,
        price: comparableProperty.attributes.price as number,
        listingEvents: comparableProperty.attributes.listingEvents?.map((listingEvent) => ({
          eventType: listingEvent.eventType,
          date: listingEvent.date.toString(),
          price: listingEvent.relatedPrice as number,
        })) ?? null,
        bedrooms: comparableProperty.attributes.numberOfBedrooms as number,
        bathrooms: comparableProperty.attributes.numberOfBathrooms as number,
        type: comparableProperty.attributes.propertyType as string,
        councilTaxBand: comparableProperty.attributes.councilTaxBand ?? null,
        internalArea: comparableProperty.attributes.internalAreaSquareMetres ?? null,
        internalAreaSqFt: comparableProperty.attributes.internalAreaSquareMetres ? Math.round(comparableProperty.attributes.internalAreaSquareMetres * 10.7639) : null,
        pricePerSqFt: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price ? Math.round((comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres) * 10.7639) : null,
        pricePerSqM: comparableProperty.attributes.internalAreaSquareMetres && comparableProperty.attributes.price ? Math.round(comparableProperty.attributes.price / comparableProperty.attributes.internalAreaSquareMetres) : null,
        outdoorSpace: comparableProperty.attributes.outdoorSpaceAreaSquareMetres ?? null,
        outdoorSpaceSqFt: comparableProperty.attributes.outdoorSpaceAreaSquareMetres ? Math.round(comparableProperty.attributes.outdoorSpaceAreaSquareMetres * 10.7639) : null,
        distanceFromPoint: comparableProperty.attributes.distanceFromPoint ?? null,
        location: {
          lng: comparableProperty.attributes.location?.coordinates?.longitude ?? 0,
          lat: comparableProperty.attributes.location?.coordinates?.latitude ?? 0,
        },
        status: comparableProperty.attributes.status as string,
        listedDate: comparableProperty.attributes.listedDate as string,
        transactionDate: null,
        agent: comparableProperty.attributes.agent as ComparablesDetailsData['agent'],
        tenure: comparableProperty.attributes.tenure as ComparablesDetailsData['tenure'],
      })) ?? [];

    if (comparables.value.sales.comparables?.length) {
      state.content.comparables.isEmpty = false;
    }
  };

  const getSectionDisplay = (tabId: ReportTabEnum, sectionId?: SectionId) => {
    const tab: any = state.content[tabId];

    if (tab && !sectionId) {
      return tab.display;
    }

    if (tab && sectionId && sectionId in tab) {
      return tab[sectionId]?.display ?? true;
    }

    return true;
  };

  const setCurrentTab = (tab: ReportTabEnum) => {
    state.currentTab = tab;
  };

  const setMarketStatQueryStatusesToSuccess = () => {
    marketStatsStore.salesEstimatedPriceChangeQueryStatus.setStatus('success');
    marketStatsStore.salesVolumeOfSoldPropertiesQueryStatus.setStatus('success');
    marketStatsStore.salesAverageTimeOnMarketQueryStatus.setStatus('success');
    marketStatsStore.salesAveragePricePerSqftQueryStatus.setStatus('success');
    marketStatsStore.lettingsEstimatedPriceChangeQueryStatus.setStatus('success');
    marketStatsStore.lettingsAverageTimeOnMarketQueryStatus.setStatus('success');
    marketStatsStore.lettingsVolumeOfLetPropertiesQueryStatus.setStatus('success');
    marketStatsStore.lettingsAveragePropertyYieldQueryStatus.setStatus('success');
  };

  const hydrateReportContent = (reportContent: ReportContentData) => {
    state.content = reportContent;
  };

  const initialiseExistingReport = async (report: ReportData) => {
    if (!report.latestRevision) {
      return;
    }

    reportSaveQueryStatus.setStatus('success');
    streetViewStore.savingNewStreetViewPositionQueryStatus.setStatus('idle');

    state.id = report.id;
    state.digitalShortUrl = report.digitalShortUrl?.url as string;
    state.leadCaptureShortUrl = report.leadCaptureShortUrl?.url as string;
    hydrateReportContent(report.latestRevision.content);
    state.accountId = report.account?.id ?? null;
    state.templateId = report.reportTemplate?.id ?? null;
    state.agentId = report.agent?.id ?? null;
    state.accountId = report.account?.id ?? null;

    hasStalePdf.value = report.hasStalePdf;

    if (state.content.overview.mainImageSource === 'unavailable' && state.content.overview.streetView.url) {
      updatePropertyImage('streetview', state.content.overview.streetView.url);
    }

    if (comparables.value.isEmpty) {
      await retrieveInitialSaleComparables();
      await retrieveInitialCompletedComparables();
      await retrieveInitialRentalComparables();
    } else {
      completedComparablesQueryStatus.setStatus('success');
      saleComparablesQueryStatus.setStatus('success');
      rentalComparablesQueryStatus.setStatus('success');
    }

    if (state.content.market.isEmpty) {
      await marketStatsStore.fetchStats();
    } else {
      setMarketStatQueryStatusesToSuccess();
    }

    if (report.uploadedImages) {
      refreshUploadedImages(report.uploadedImages);
    }

    reportEditStore.setOrderFromSavedReport(report);

    reportLoadedQueryStatus.setStatus('success');

    if (!getSectionDisplay('overview')) {
      const otherReportSections = ['comparables', 'localArea', 'gallery', 'market', 'about'];

      const nextSection = otherReportSections.find((section) => state.content[section as ReportTabEnum]?.display);
      setCurrentTab(nextSection as ReportTabEnum);
    }
  };

  const initialisePublicReport = async (report: ReportData, hideDigitalCover: boolean) => {
    if (!report.content) {
      return;
    }

    state.public = true;
    state.hideCover = hideDigitalCover;
    hydrateReportContent(report.content);
    state.id = report.id;
    initialiseTenureDataCard();
    propertyImageQueryStatus.setStatus('success');
    saleComparablesQueryStatus.setStatus('success');
    completedComparablesQueryStatus.setStatus('success');
    rentalComparablesQueryStatus.setStatus('success');

    if (state.content.market.isEmpty) {
      await marketStatsStore.fetchStats();
    } else {
      setMarketStatQueryStatusesToSuccess();
    }

    if (state.content.gallery.isEmpty) {
      state.content.gallery.display = false;
    }
  };

  const toggleComparables = (sectionId: ComparableTypes) => {
    if (state.content.comparables[sectionId].display) {
      comparablesStore.updateComparableType(sectionId);
      return;
    }

    const types = ['sales', 'completed', 'lettings'] as ComparableTypes[];

    const remainingSections = types.filter((section: ComparableTypes) => section !== sectionId && comparables.value[section as ComparableTypes].display) as ComparableTypes[];

    const sortedSections: ComparableTypes[] = remainingSections.sort((a: ComparableTypes, b: ComparableTypes) => (comparables.value[a].order - comparables.value[b].order));

    comparablesStore.updateComparableType(sortedSections[0] ?? null);

    comparablesStore.state.comparablesQueryResults = [];
  };

  const toggleSectionVisibility = (tabId: ReportTabEnum, sectionId: SectionId | null, toggleState: boolean) => {
    const tab: any = state.content[tabId];

    if (tab && !sectionId) {
      tab.display = toggleState;
    }

    if (tab && sectionId && sectionId in tab) {
      tab[sectionId] = {
        ...tab[sectionId],
        display: toggleState,
      };
    }

    if (tabId === 'comparables' && sectionId) {
      toggleComparables(sectionId as ComparableTypes);
    }

    saveRevision();
  };

  const showSection = (tabId: ReportTabEnum, sectionId?: SectionId) => {
    const section = sectionId ?? null;
    toggleSectionVisibility(tabId, section, true);
    saveRevision();
  };

  const hideSection = (tabId: ReportTabEnum, sectionId?: SectionId) => {
    toggleSectionVisibility(tabId, sectionId ?? null, false);

    if (!sectionId && currentTab.value === tabId) {
      const index = reportEditStore.sections.findIndex((section) => section.id === tabId);
      const previousSections = slice(reportEditStore.sections, 0, index).reverse();
      const nextSections = slice(reportEditStore.sections, index + 1);
      const allReportSections = {
        overview: state.content.overview,
        comparables: state.content.comparables,
        localArea: state.content.localArea,
        gallery: state.content.gallery,
        market: state.content.market,
        about: state.content.about,
      };
      const nextSection = [...previousSections, ...nextSections].find((section) => allReportSections[section.id as keyof typeof allReportSections]?.display);

      if (!nextSection) {
        return;
      }

      setCurrentTab(nextSection.id as ReportTabEnum);
    }
    saveRevision();
  };

  const updateReportTemplateId = (template: ReportTemplateData) => {
    state.templateId = template.id;
  };

  const templateChanged = (template: ReportTemplateData, onSuccess?: () => void) => {
    updateReportTemplateId(template);
    setCurrentTab('overview');

    state.content.settings = {
      ...state.content.settings,
      anonymiseAddress: template.content.settings.anonymiseAddress,
      hideEmptySections: template.content.settings.hideEmptySections,
      hideContactDetails: template.content.settings.hideContactDetails,
    };

    router.put(route('reports.update-report-template', { report: reportId.value }), {
      reportTemplate: template.id,
    }, {
      preserveState: preserveState.value,
      preserveScroll: true,
      onSuccess: () => onSuccess?.(),
    });
  };

  const setReportId = (id: string) => {
    state.id = id;
  };

  const setSelectedTab = (tab: ReportTabEnum | null) => {
    state.selectedTab = tab;
  };

  const deleteReport = (id: string) => {
    router.delete(route('reports.destroy', id), {
      preserveState: false,
    });
  };

  const updateRecipients = (recipients: string[]) => {
    state.content.settings.recipients = recipients;
  };

  const updateAgent = (newAgentId: string) => {
    state.agentId = newAgentId;
  };

  const updateReportMessage = (message: string) => {
    state.content.settings.reportMessage = message;
  };

  const updateAnonymiseAddress = (value: boolean) => {
    state.content.settings.anonymiseAddress = value;
    saveRevision();
  };

  const updateHideContactDetails = (value: boolean) => {
    state.content.settings.hideContactDetails = value;
    saveRevision();
  };

  const updateParking = (type: ReportParkingType, description: string) => {
    state.content.overview.parking.type = type;
    state.content.overview.parking.description = description;
    saveRevision();
  };

  const getSectionIsEmpty = (tabId: ReportTabEnum, sectionId: SectionId) => {
    const tab: any = state.content[tabId];

    if (tab && sectionId && sectionId in tab) {
      return tab[sectionId]?.isEmpty ?? false;
    }

    return false;
  };

  const showSections = () => {
    const tabs = [
      'overview',
      'comparables',
      'localArea',
      'market',
      'about',
    ] as ReportTabEnum[];

    tabs.forEach((tab) => {
      const sections = Object.keys(state.content[tab])
        .filter((key) => {
          const subsection = state.content[tab][key as keyof OrderableSubTabs] as ReportSubSection;
          return subsection && typeof subsection === 'object';
        })
        .filter((key) => {
          const section = (state.content[tab][key as keyof OrderableSubTabs]) as ReportSubSection;
          return ('isEmpty' in section) && section.isEmpty;
        }) as SectionId[];

      sections.forEach((section: SectionId) => {
        showSection(tab, section);
      });
    });
  };

  const hideSections = () => {
    const tabs = [
      'overview',
      'comparables',
      'localArea',
      'market',
      'about',
    ] as ReportTabEnum[];

    tabs.forEach((tab) => {
      const sections = Object.keys(state.content[tab])
        .filter((key) => getSectionIsEmpty(tab, key as SectionId)) as SectionId[];

      sections.forEach((section: SectionId) => {
        hideSection(tab, section);
      });
    });
  };

  const updateHideEmptySections = (value: boolean) => {
    state.content.settings.hideEmptySections = value;

    if (value) {
      hideSections();
      return;
    }

    showSections();
  };

  const setQrCodeBase64 = (qrCodeType: 'digitalQrCode' | 'leadCaptureQrCode', base64: string) => {
    state[qrCodeType] = base64;
  };

  const tabChanged = () => {
    comparablesStore.toggleShowFilters(false);
    resetScrollPosition();
  };

  const updateComparableNote = (type: ComparableTypes, id: string, note?: string) => {
    const comparableToUpdate = state.content.comparables[type].comparables?.find((comparable) => comparable.id === id);

    if (!comparableToUpdate) {
      return;
    }

    comparableToUpdate.notes = note === '' ? undefined : note;

    saveRevision();
  };

  const setBookViewingUrl = (url: string) => {
    state.bookViewingUrl = url;
  };

  return {
    about,
    accountId,
    updateComparableNote,
    agentId,
    address,
    bookViewingUrl,
    comparables,
    comparableIsSelected,
    completedComparablesQueryStatus,
    content: {
      overview,
      comparables,
      localArea,
      gallery,
      market,
      about,
    },
    currentTab,
    deleteReport,
    digitalQrCode,
    digitalShortUrl,
    pricePerSqft,
    pricePerSqm,
    gallery,
    getSectionDisplay,
    hasStalePdf,
    hideCover,
    hideSection,
    hydrateReportContent,
    initialiseExistingReport,
    initialisePublicReport,
    initialiseTenureDataCard,
    isPublicReport,
    leadCaptureQrCode,
    leadCaptureShortUrl,
    localArea,
    location,
    market,
    marketHistory,
    overview,
    polygons,
    propertyId,
    propertyImageQueryStatus,
    refreshUploadedImages,
    rentalComparablesQueryStatus,
    reportId,
    reportLoadedQueryStatus,
    reportSaveQueryStatus,
    reportTitle,
    resetPropertyImage,
    saleComparablesQueryStatus,
    showMainImage,
    saveReport,
    saveRevision,
    selectedTab,
    setBookViewingUrl,
    setCurrentTab,
    setMarketStatQueryStatusesToSuccess,
    setOrder,
    setQrCodeBase64,
    setReportId,
    setSelectedTab,
    setStreetViewPosition,
    settings,
    showSection,
    state,
    tabChanged,
    templateChanged,
    templateId,
    toggleComparableSelected,
    toggleHasStalePdf,
    toggleShowMainImage,
    updateAgent,
    updateAnonymiseAddress,
    updateBuildData,
    updateConstructionMaterialData,
    updateCouncilTaxData,
    updateDescriptionData,
    updateHideEmptySections,
    updateHideContactDetails,
    updateLettingsMarketStats,
    updatePropertyImage,
    updatePropertyValueData,
    updateRecipients,
    updateReportMessage,
    updateReportTemplateId,
    updateSalesMarketStats,
    updateStreetViewPosition,
    updateTenureData,
    updateTitleDeedsData,
    updateUtilitiesData,
    updateOutdoorSpaceData,
    updateParking,
    updatePropertySizeData,
    setPreserveState,
  };
});
